import React from "react";
import {
  TextField,
  Switch,
  FormControlLabel,
  Paper,
  Button,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { Add, Delete, Save } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";

const SettingField = ({
  setting,
  editedSettings,
  handleChange,
  handleNestedChange,
  handleAddItem,
  handleRemoveItem,
  handleSave,
}: any) => {
  const { t } = useTranslation();
  const renderTextField = () => (
    <TextField
      fullWidth
      variant="outlined"
      value={editedSettings[setting.key] ?? setting.value}
      onChange={(e) => handleChange(setting.key, e.target.value)}
      className="mb-2"
    />
  );

  const renderRichTextEditor = () => (
    <ReactQuill
      theme="snow"
      value={editedSettings[setting.key] ?? setting.value}
      onChange={(value) => handleChange(setting.key, value)}
    />
  );

  const renderBooleanSwitch = () => (
    <FormControlLabel
      control={
        <Switch
          checked={editedSettings[setting.key] ?? setting.value}
          onChange={(e) => handleChange(setting.key, e.target.checked)}
        />
      }
      label={setting.key}
    />
  );

  const renderArrayFields = () => {
    const itemsArray = editedSettings[setting.key] || setting.value || [];

    return itemsArray.map((item: string, index: number) => (
      <Box key={index} display="flex" alignItems="center" gap={1}>
        <TextField
          fullWidth
          variant="outlined"
          value={editedSettings[setting.key]?.[index] ?? item ?? ""}
          onChange={(e) => {
            const newArray = [
              ...(editedSettings[setting.key] || setting.value),
            ];
            newArray[index] = e.target.value;
            handleChange(setting.key, newArray);
          }}
          className="mb-2"
        />
        <IconButton
          color="secondary"
          onClick={() => handleRemoveItem(setting.key, index)}
        >
          <Delete />
        </IconButton>
      </Box>
    ));
  };

  const renderObjectFields = () =>
    Object.keys(setting.value).map((field) =>
      field === "content" ? (
        <ReactQuill
          key={t(field)}
          theme="snow"
          value={editedSettings[setting.key]?.[field] ?? setting.value[field]}
          onChange={(value) => handleNestedChange(setting.key, 0, field, value)}
        />
      ) : (
        <TextField
          key={t(field)}
          fullWidth
          variant="outlined"
          label={t(field)}
          value={editedSettings[setting.key]?.[field] ?? setting.value[field]}
          onChange={(e) =>
            handleNestedChange(setting.key, 0, field, e.target.value)
          }
          className="mb-2"
        />
      )
    );

  const renderArrayOfObjects = () => {
    const items = editedSettings[setting.key] || setting.value || [];

    return items.map((item: any, index: number) => (
      <Paper key={index} elevation={2} style={{ padding: 10, marginBottom: 8 }}>
        {Object.keys(item).map((field) => (
          <TextField
            key={t(field)}
            fullWidth
            variant="outlined"
            label={t(field)}
            value={
              editedSettings[setting.key]?.[index]?.[field] ?? item[field] ?? ""
            }
            onChange={(e) => {
              const updatedValue = JSON.parse(
                JSON.stringify(editedSettings[setting.key] || setting.value)
              );
              updatedValue[index][field] = e.target.value;
              handleChange(setting.key, updatedValue);
            }}
            className="mb-2"
          />
        ))}
        <IconButton
          color="secondary"
          onClick={() => handleRemoveItem(setting.key, index)}
        >
          <Delete />
        </IconButton>
      </Paper>
    ));
  };

  return (
    <Paper elevation={3} style={{ padding: 16, borderRadius: 8 }}>
      <Typography variant="h6" gutterBottom>
        {t(setting.key)}
      </Typography>

      {typeof setting.value === "string"
        ? setting.value.includes("<")
          ? renderRichTextEditor()
          : renderTextField()
        : Array.isArray(setting.value)
        ? setting.value[0] && typeof setting.value[0] === "object"
          ? renderArrayOfObjects()
          : renderArrayFields()
        : typeof setting.value === "object"
        ? renderObjectFields()
        : typeof setting.value === "boolean"
        ? renderBooleanSwitch()
        : null}

      <Box className="flex justify-between">
        {Array.isArray(setting.value) && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() =>
              handleAddItem(
                setting.key,
                typeof setting.value[0] === "object" ? {} : ""
              )
            }
            style={{ marginTop: 10 }}
          >
            {t("add_item")}
          </Button>
        )}

        <Box mt={2} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={() => handleSave(setting.key)}
          >
            {t("save_changes")}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default SettingField;
